import { api } from './client';

/**
 * §6.E "Schedule a game with friends" — a host picks a time, adds friends'
 * emails, and gets a shareable link + add-to-calendar. At game time one tap
 * drops everyone into a shared multiplayer wheel room.
 *
 * <p>This is the typed web-play client for the scheduled-games endpoints
 * (built in parallel on the server). It mirrors the fetch/error style of
 * {@link ./rooms} — every call funnels through {@link api}, which throws an
 * {@link ApiError} on a non-2xx and parses JSON otherwise. The actual room
 * create/join is NOT reimplemented here: the join flow reuses
 * {@code createRoom} / {@code joinWheelRoomByCode} from {@link ./rooms} +
 * {@code App.tsx}; this client only owns the schedule lifecycle (create /
 * read / list / claim-room / cancel).
 */

/** Lifecycle of a scheduled game. {@code SCHEDULED} = upcoming; {@code LIVE}
 *  = joinable now (a room may or may not be claimed yet); {@code CANCELLED}
 *  by the host. Treated as an open string union so an unknown server value
 *  never breaks a build — the UI keys off {@code joinableNow}/{@code roomCode}
 *  rather than parsing this exhaustively. */
export type ScheduledGameStatus = 'SCHEDULED' | 'LIVE' | 'CANCELLED' | (string & {});

/**
 * A scheduled game as the server returns it. The public read
 * ({@link getScheduledGame}) omits invitee emails (privacy) and carries an
 * {@code inviteeCount} instead; the host's own list keeps the same shape.
 */
export interface ScheduledGame {
  id: string;
  /** Short shareable code embedded in {@code shareUrl} (`?game=CODE`). */
  inviteCode: string;
  hostPlayerId: string;
  hostName: string;
  /** Reel-skin theme id for the room spun up at game time. */
  themeId: string;
  /** Optional host-supplied title (e.g. "Friday night spins"). */
  title: string;
  /** Epoch milliseconds — when the game is scheduled to start. */
  scheduledFor: number;
  status: ScheduledGameStatus;
  /** The claimed wheel-room invite code (e.g. "GTC-4F2A"), once a room has
   *  been spun up for this game; null/absent until then. */
  roomCode: string | null;
  /** True once the start time has arrived (or the host opened it early) so
   *  invitees may join. The card flips its CTA on this. */
  joinableNow: boolean;
  /** Number of invited emails — the public read never exposes the addresses. */
  inviteeCount: number;
  /** `https://play.goodtime.casino/?game={inviteCode}` — what the host shares. */
  shareUrl: string;
  /** `https://api.goodtime.casino/v1/scheduled-games/{inviteCode}.ics` — the
   *  add-to-calendar download. */
  icsUrl: string;
  /** Whether the server actually emails invitees. When false, the host shares the
   *  link themselves — the UI must not claim "we'll email them". */
  emailEnabled?: boolean;
}

/** Request body for {@link createScheduledGame}. */
export interface CreateScheduledGameInput {
  hostPlayerId: string;
  /** Reel-skin theme for the room; the server defaults to the host's current
   *  theme when omitted. */
  themeId?: string;
  title?: string;
  /** Epoch milliseconds. */
  scheduledFor: number;
  /** Optional friend emails to invite (link-only is fine — pass none). */
  inviteeEmails?: string[];
}

/**
 * Schedule a new game. Returns the full {@link ScheduledGame} including the
 * {@code shareUrl} + {@code icsUrl} the success view celebrates.
 */
export function createScheduledGame(input: CreateScheduledGameInput): Promise<ScheduledGame> {
  return api<ScheduledGame>('/scheduled-games', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Public read of a scheduled game by its share code (the {@code ?game=CODE}
 * an invitee lands on). Carries {@code inviteeCount} but never the invitee
 * emails. Rejects (throws {@link ApiError}) on an unknown / cancelled code.
 */
export function getScheduledGame(code: string): Promise<ScheduledGame> {
  return api<ScheduledGame>(`/scheduled-games/${encodeURIComponent(code)}`);
}

/** The host's upcoming scheduled games (most-recent-first per the server). */
export function listMyScheduledGames(playerId: string): Promise<ScheduledGame[]> {
  return api<ScheduledGame[]>(
    `/players/${encodeURIComponent(playerId)}/scheduled-games`,
  );
}

/**
 * Bind a freshly-created wheel room to this scheduled game so every invitee
 * lands in the SAME table. Idempotent + race-safe: if a room was already
 * claimed (a friend got there first), the server returns that existing
 * {@code roomCode} instead of the one we proposed — the caller then joins
 * THAT code rather than its own. Resolve the by-{@code code} share code, not
 * the internal id.
 */
export function claimScheduledGameRoom(
  code: string,
  playerId: string,
  roomCode: string,
): Promise<{ roomCode: string }> {
  return api<{ roomCode: string }>(
    `/scheduled-games/${encodeURIComponent(code)}/claim-room`,
    {
      method: 'POST',
      body: JSON.stringify({ playerId, roomCode }),
    },
  );
}

/**
 * Cancel a scheduled game (host only). Keyed on the internal {@code id} (not
 * the share code), matching the server contract. Resolves on a 200.
 */
export async function cancelScheduledGame(id: string, playerId: string): Promise<void> {
  await api<unknown>(`/scheduled-games/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ playerId }),
  });
}
